import { ModuleErrorBoundary } from '@components/ErrorBoundary';
import UnreadTextCountHost from './UnreadTextCountHost';

/**
 * Zero-sized clip that hides the count producer.
 *
 * `Loader` mounts the real `comm-hub-text-list` bundle -- a full conversation
 * list plus its stylesheet -- so "hidden" has to be a property of this
 * container, not of the child. Three mechanisms are needed and none is
 * redundant, because each covers a channel the others leave open:
 *
 * - `overflow: hidden` on a zero-sized box hides the list visually;
 * - `aria-hidden` takes it out of the accessibility tree, which the clip alone
 *   does not;
 * - `inert` takes it out of the tab order, which neither of the other two does.
 *   Without it the list's rows, buttons and inputs stay tabbable, so every
 *   keyboard user on every CCPro route walks a run of focus stops they cannot
 *   see; focusable content under `aria-hidden` is also an axe
 *   `aria-hidden-focus` violation in its own right.
 *
 * `inert` is spread in because @types/react 18.2 has no declaration for it.
 * `inert=''` is the spec's boolean-attribute form and React forwards unknown
 * string attributes straight to `setAttribute`.
 *
 * Never `display: none` and never a route-conditional branch. `display: none`
 * does not reliably keep child effects, timers and subscriptions running the way
 * a clipped subtree does, and an unmounted branch drops the Pusher lease
 * entirely -- the count must keep flowing on every route. `inert` is safe on
 * that score: it governs focus and hit-testing only, never rendering or effects.
 */
const HIDDEN_MOUNT_STYLE = {
  height: 0,
  width: 0,
  overflow: 'hidden',
} as const;

/** See the note above -- `inert` is absent from @types/react 18.2. */
const INERT_ATTRIBUTE = { inert: '' } as const;

/**
 * The feature's app-lifetime mount point. Permanent, route-independent and
 * logic-free by design: the entitlement gate lives inside `UnreadTextCountHost`,
 * not on the JSX here.
 *
 * The error boundary sits *inside* the clip rather than around it. Both
 * placements contain a throw, but only this one stays harmless if the fallback
 * is ever mis-specified -- a boundary outside the clip that fell through to the
 * default error UI would mount a visible "... Error" box into normal page flow
 * on every CCPro route.
 *
 * `fallback={() => null}` is deliberate and `fallback={null}` is a trap:
 * `ModuleErrorBoundary.render()` does `if (fallback)`, and `null` is falsy, so
 * it would fall straight through to the default visible error box. A function is
 * truthy and gets invoked. Reporting to `DiagnosticsModule` happens in
 * `componentDidCatch` either way.
 *
 * One thing the clip cannot cover: web-components' `register()` attaches each
 * MFE's modal/popup portal to `document.body`
 * (`document.body.appendChild(portalElement).attachShadow(...)`) and hands it to
 * the MFE as the Anvil portal target, so it lands outside this container.
 * Anything the hidden list portals would render full-size on whatever route is
 * showing. Nothing it does today portals unprompted, and the portal host is
 * removed on unmount, so this is a caveat rather than a live defect -- but the
 * clip is not the airtight boundary it looks like.
 */
const UnreadTextCountMount = () => (
  <div style={HIDDEN_MOUNT_STYLE} aria-hidden='true' {...INERT_ATTRIBUTE}>
    <ModuleErrorBoundary moduleName='UnreadTextCount' fallback={() => null}>
      <UnreadTextCountHost />
    </ModuleErrorBoundary>
  </div>
);

export default UnreadTextCountMount;
